import { createContext, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { AuthContext as AuthContextType } from '../types';
import { setAuthTokenGetter } from '../lib/api-client';

export interface AuthContextValue extends AuthContextType {
  setToken: (token: string) => void;
  isAdmin: boolean;
  isTenant: boolean;
  logout: () => void;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_STORAGE_KEY = 'kpolit_auth_token';

interface JwtPayload {
  role?: string;
  serviceId?: string;
  services?: string;
  displayName?: string;
  sub?: string;
  name?: string;
  [key: string]: unknown;
}

/**
 * Decodes a JWT token payload (base64url) without external libraries.
 * Returns the parsed payload object or null if decoding fails.
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    const payload = parts[1]!;
    // Base64url → Base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonStr = atob(base64);
    return JSON.parse(jsonStr) as JwtPayload;
  } catch {
    return null;
  }
}

/**
 * Extracts AuthContext fields from a decoded JWT payload.
 */
function extractAuthFromPayload(payload: JwtPayload, token: string): AuthContextType {
  // The auth-service JWT has: sub (email), services (serviceCode)
  // Treat "comms-admin" serviceCode as Admin_User, anything else as Tenant_User
  const serviceCode = payload.services ?? '';
  const role = serviceCode === 'comms-admin' ? 'Admin_User' : 'Tenant_User';
  const serviceId = serviceCode;
  const displayName = payload.sub ?? payload.displayName ?? payload.name ?? 'User';

  return { role, serviceId, token, displayName };
}

/**
 * Filters a dataset to only include items belonging to a specific tenant.
 * Used by Tenant_User views to scope data to their own serviceId/ownerId.
 *
 * @param items - Array of items with a key field to filter on
 * @param tenantId - The authenticated tenant's serviceId
 * @param key - The property name to match against tenantId (e.g., 'serviceId' or 'ownerId')
 */
export function filterByTenant<T>(
  items: T[],
  tenantId: string,
  key: keyof T,
): T[] {
  if (!tenantId) return [];
  return items.filter((item) => item[key] === tenantId);
}

interface AuthProviderProps {
  children: ReactNode;
  /** Optional initial token for testing/dev purposes */
  initialToken?: string;
}

export function AuthProvider({ children, initialToken }: AuthProviderProps) {
  const [token, setTokenState] = useState<string>(() => {
    if (initialToken) return initialToken;
    return localStorage.getItem(TOKEN_STORAGE_KEY) ?? '';
  });

  const setToken = useCallback((newToken: string) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    setTokenState(newToken);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setTokenState('');
  }, []);

  // Register the token getter with the API client so all requests include auth
  useEffect(() => {
    setAuthTokenGetter(() => token || null);
  }, [token]);

  const contextValue = useMemo<AuthContextValue>(() => {
    if (!token) {
      return {
        role: 'Tenant_User',
        serviceId: '',
        token: '',
        displayName: '',
        setToken,
        isAdmin: false,
        isTenant: true,
        logout,
      };
    }

    const payload = decodeJwtPayload(token);
    if (!payload) {
      return {
        role: 'Tenant_User',
        serviceId: '',
        token,
        displayName: '',
        setToken,
        isAdmin: false,
        isTenant: true,
        logout,
      };
    }

    const auth = extractAuthFromPayload(payload, token);

    return {
      ...auth,
      setToken,
      isAdmin: auth.role === 'Admin_User',
      isTenant: auth.role === 'Tenant_User',
      logout,
    };
  }, [token, setToken, logout]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
